import axios from 'axios';

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  serviceInterest?: string;
  messageTo: string;
  message: string;
  source?: string;
}

interface ContactResponse {
  success: boolean;
  message: string;
  data?: {
    id: number;
    submittedAt: string;
    estimatedResponseTime: string;
  };
  errors?: string[];
}

interface ContactMessage {
  id: number;
  name: string;
  email: string;
  serviceInterest: string;
  messageTo: string;
  status: string;
  priority: string;
  submittedAt: string;
  respondedAt?: string;
  followUpDate?: string;
  syncStatus: string;
}

interface ContactStats {
  totalMessages: number;
  newMessages: number;
  recentMessages: number;
  d365Sync: {
    pending: number;
    failed: number;
  };
  serviceInterestStats: Array<{
    service: string;
    count: number;
  }>;
  messageToStats: Array<{
    destination: string;
    count: number;
  }>;
}

class ContactService {
  private baseURL = process.env.NODE_ENV === 'development' 
    ? 'http://localhost:5002/api/contact' 
    : 'https://www.crmonce.com/api/contact';

  /**
   * Submit contact form
   */
  async submitContactForm(formData: ContactFormData): Promise<ContactResponse> {
    try {
      const response = await axios.post(`${this.baseURL}/submit`, formData);
      return response.data;
    } catch (error: any) {
      console.error('Error submitting contact form:', error);
      if (error.response?.data) {
        throw error.response.data;
      }
      throw new Error('Failed to submit contact form. Please try again.');
    }
  }

  /**
   * Get contact message by ID
   */
  async getContactMessage(id: number): Promise<ContactMessage> {
    try {
      const response = await axios.get(`${this.baseURL}/message/${id}`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching contact message:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch message details');
    }
  }

  /**
   * Get contact form statistics
   */
  async getContactStats(): Promise<ContactStats> {
    try {
      const response = await axios.get(`${this.baseURL}/stats`);
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching contact stats:', error);
      throw new Error('Failed to fetch contact statistics');
    }
  }

  /**
   * Test D365 connection
   */
  async testD365Connection(): Promise<{ success: boolean; message: string; data?: any; error?: string }> {
    try {
      const response = await axios.get(`${this.baseURL}/test/d365`);
      return response.data;
    } catch (error: any) {
      console.error('Error testing D365 connection:', error);
      return {
        success: false,
        message: 'Failed to test D365 connection',
        error: error.message
      };
    }
  }

  /**
   * Trigger D365 sync
   */
  async triggerD365Sync(action: 'sync_pending' | 'retry_failed' | 'sync_specific', messageId?: number): Promise<any> {
    try {
      const response = await axios.post(`${this.baseURL}/sync/d365`, {
        action,
        messageId
      });
      return response.data;
    } catch (error: any) {
      console.error('Error triggering D365 sync:', error);
      throw new Error(error.response?.data?.message || 'Failed to trigger D365 sync');
    }
  }

  /**
   * Get recent messages
   */
  async getRecentMessages(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    priority?: string;
  }): Promise<{
    messages: ContactMessage[];
    total: number;
    pagination: {
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    try {
      const response = await axios.get(`${this.baseURL}/messages/recent`, { params });
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching recent messages:', error);
      throw new Error('Failed to fetch recent messages');
    }
  }

  /**
   * Validate email format
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone number format
   */
  isValidPhone(phone: string): boolean {
    if (!phone) return true; // Phone is optional
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  }

  /**
   * Format contact form data
   */
  formatContactFormData(data: Partial<ContactFormData>): ContactFormData {
    return {
      firstName: data.firstName?.trim() || '',
      lastName: data.lastName?.trim() || '',
      email: data.email?.toLowerCase().trim() || '',
      phone: data.phone?.trim() || undefined,
      company: data.company?.trim() || undefined,
      serviceInterest: data.serviceInterest || 'general_inquiry',
      messageTo: data.messageTo || 'general_inquiry',
      message: data.message?.trim() || '',
      source: data.source || 'contact_page'
    };
  }

  /**
   * Get service interest options
   */
  getServiceInterestOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'general_inquiry', label: 'General Inquiry' },
      { value: 'dynamics365_implementation', label: 'Dynamics 365 Implementation' },
      { value: 'power_platform_development', label: 'Power Platform Development' },
      { value: 'consulting_services', label: 'Consulting Services' },
      { value: 'professional_training', label: 'Professional Training' },
      { value: 'support_maintenance', label: 'Support & Maintenance' },
      { value: 'custom_development', label: 'Custom Development' },
      { value: 'data_migration', label: 'Data Migration' },
      { value: 'integration_services', label: 'Integration Services' }
    ];
  }

  /**
   * Get message destination options
   */
  getMessageToOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'general_inquiry', label: 'General Inquiry' },
      { value: 'sales_team', label: 'Sales Team' },
      { value: 'support_team', label: 'Support Team' },
      { value: 'consulting_team', label: 'Consulting Team' },
      { value: 'training_team', label: 'Training Team' },
      { value: 'technical_team', label: 'Technical Team' }
    ];
  }

  /**
   * Get priority color class
   */
  getPriorityColorClass(priority: string): string {
    const colorMap = {
      'low': 'text-gray-600',
      'medium': 'text-blue-600',
      'high': 'text-orange-600',
      'urgent': 'text-red-600'
    };
    return colorMap[priority as keyof typeof colorMap] || 'text-gray-600';
  }

  /**
   * Get status color class
   */
  getStatusColorClass(status: string): string {
    const colorMap = {
      'new': 'text-blue-600 bg-blue-100',
      'contacted': 'text-yellow-600 bg-yellow-100',
      'qualified': 'text-green-600 bg-green-100',
      'converted': 'text-purple-600 bg-purple-100',
      'closed': 'text-gray-600 bg-gray-100'
    };
    return colorMap[status as keyof typeof colorMap] || 'text-gray-600 bg-gray-100';
  }

  /**
   * Get sync status color class
   */
  getSyncStatusColorClass(syncStatus: string): string {
    const colorMap = {
      'pending': 'text-yellow-600 bg-yellow-100',
      'synced': 'text-green-600 bg-green-100',
      'failed': 'text-red-600 bg-red-100',
      'skipped': 'text-gray-600 bg-gray-100'
    };
    return colorMap[syncStatus as keyof typeof colorMap] || 'text-gray-600 bg-gray-100';
  }
}

export default new ContactService();
export type { ContactFormData, ContactResponse, ContactMessage, ContactStats };
